import React from 'react';
import { FormattedMessage } from 'react-intl';

import type { ButtonSizes, ButtonVariants } from '@xingternal/button';
import { Button } from '@xingternal/button';
import { IconNotificationCenter, IconNotificationFilled } from '@xingternal/icons';

import { useLoginStates } from '@/hooks';

import { useSearchAlertModal, useSearchAlertsContext } from '../../contexts';
import { useEditAlertMode } from '../../hooks';

type Props = {
  className?: string;
  variant?: ButtonVariants;
  size?: ButtonSizes;
  showIcon?: boolean;
  email?: string;
  propActionOrigin?: string;
  inDialog?: boolean;
  previousValidationOnClick?: () => boolean;
};

export const AlertButton: React.VFC<Props> = ({
  className = '',
  variant = 'secondary',
  showIcon = true,
  size,
  email,
  propActionOrigin,
  inDialog,
  previousValidationOnClick = () => true,
}) => {
  const { isLoggedIn } = useLoginStates();
  const { toggleAlert, isAlertActive, areAlertsInitialized } = useSearchAlertsContext();
  const { isModalShown, isValidQuery, toggleModal, openPropActionOrigin } = useSearchAlertModal();
  const { isEditingSearchAlert } = useEditAlertMode();

  const buttonIcon = isAlertActive ? IconNotificationFilled : IconNotificationCenter;

  const isLoggedOutAndModalIsNotShown = !isLoggedIn && !isModalShown;

  const onClickHandler = (): void => {
    if (!isValidQuery || isLoggedOutAndModalIsNotShown) {
      toggleModal({ propActionOrigin });
      return;
    }

    if (previousValidationOnClick()) {
      const origin = propActionOrigin ?? openPropActionOrigin;
      toggleAlert({
        ...(origin && { propTrackActionData: { PropActionOrigin: origin } }),
        ...(!isLoggedIn && { email }),
      });

      if (isModalShown) {
        toggleModal();
      }
    }
  };

  const defaultLocaleKey = isAlertActive
    ? 'JOBS_SEARCH_ALERT_BUTTON_CREATED'
    : isEditingSearchAlert
    ? 'JOBS_SEARCH_EDIT_ALERT_SAVE_BUTTON'
    : 'JOBS_SEARCH_ALERT_BUTTON_CREATE';
  const localeKey = !isLoggedIn && inDialog ? 'JOBS_SEARCH_ALERTS_MODAL_FREQUENCY_BUTTON' : defaultLocaleKey;

  return (
    <>
      <Button
        className={className}
        size={size}
        variant={variant}
        onClick={onClickHandler}
        disabled={!areAlertsInitialized}
        {...(showIcon && { icon: buttonIcon })}
        data-cy="create-alert-button"
      >
        <FormattedMessage id={localeKey} />
      </Button>
    </>
  );
};
